import { makeAutoObservable } from 'mobx'
import { type INormalizedApiError } from 'shared/api'
import { type IResponseRcsCompliance } from 'entities/Rcs'
import { type RcsWizardStore } from 'pages/settings/pages/rcsV2/store/rcsWizardStore'

const BRAND_NAME_MAX_LENGTH = 100

export class BrandInfoStore {
  private _brandName = ''
  private _brandNameError = ''
  private _brandDescription = ''
  private _brandDescriptionError = ''
  private _messageType = ''
  private _messageTypeError = ''

  constructor(private _wizardStore: RcsWizardStore) {
    makeAutoObservable(this)
  }

  get brandName() {
    return this._brandName
  }

  get brandNameError() {
    return this._brandNameError
  }

  get brandDescription() {
    return this._brandDescription
  }

  get brandDescriptionError() {
    return this._brandDescriptionError
  }

  get messageType() {
    return this._messageType
  }

  get messageTypeError() {
    return this._messageTypeError
  }

  get messageTypeOptions() {
    return this._wizardStore.messageTypeOptions
  }

  get messageTypesLoading() {
    return this._wizardStore.messageTypesLoading
  }

  get brandNameMaxLength() {
    return BRAND_NAME_MAX_LENGTH
  }

  get isBrandNameMaxLengthReached() {
    return this._brandName.length >= this.brandNameMaxLength
  }

  get brandDescriptionMaxLength() {
    return this.brandNameMaxLength
  }

  get isBrandDescriptionMaxLengthReached() {
    return this._brandDescription.length >= this.brandDescriptionMaxLength
  }

  get isValid() {
    return !!this._brandName && !!this._brandDescription && !!this._messageType
  }

  setBrandName = (name: string) => {
    this._brandName = name
    if (this._brandNameError) this._brandNameError = ''
  }

  setBrandDescription = (description: string) => {
    this._brandDescription = description
    if (this._brandDescriptionError) this._brandDescriptionError = ''
  }

  setMessageType = (messageType: string) => {
    this._messageType = messageType
    if (this._messageTypeError) this._messageTypeError = ''
  }

  loadMessageTypes = () => {
    this._wizardStore.loadMessageTypes()
  }

  validateBrandName = () => {
    this._brandNameError = this._brandName ? '' : 'Enter a brand name'
  }

  validateBrandDescription = () => {
    this._brandDescriptionError = this._brandDescription ? '' : 'Enter a brand description'
  }

  validate = () => {
    let hasError = false

    if (!this._brandName) {
      this._brandNameError = 'Enter a brand name'
      hasError = true
    }

    if (!this._brandDescription) {
      this._brandDescriptionError = 'Enter a brand description'
      hasError = true
    }

    if (!this._messageType) {
      this._messageTypeError = 'Select a message type'
      hasError = true
    }

    return hasError
  }

  init = (data: IResponseRcsCompliance) => {
    this._brandName = data.name || ''
    this._brandDescription = data.description || ''
    this._messageType = data.message_type?.key || ''
    if (this._messageType) this.loadMessageTypes()
  }

  initErrors = (errors: INormalizedApiError) => {
    this._brandNameError = errors.getError('name')
    this._brandDescriptionError = errors.getError('description')
    this._messageTypeError = errors.getError('message_type')
  }

  reset = () => {
    this._brandName = ''
    this._brandNameError = ''
    this._brandDescription = ''
    this._brandDescriptionError = ''
    this._messageType = ''
    this._messageTypeError = ''
  }
}
